import React from "react"

const crsUrl = "https://reports.adviserinfo.sec.gov/crs/crs_309994.pdf"
const brochureUrl = "https://adviserinfo.sec.gov/firm/brochure/309994"
const investorUrl = "https://www.investor.gov/"
const adviserInfoUrl = "https://adviserinfo.sec.gov/"

const starterSections = [
    {
        question: "Given my financial situation, should I choose investment advisory services? Why or why not?",
        answer: "Clarivor's investment advisory services may fit investors who want portfolio management focused on customized, rule-based strategies using stocks, ETFs, and options, and who are comfortable receiving those services through an automated platform rather than through frequent in-person or phone-based discussions with an adviser. When Clarivor acts as your investment adviser, it has fiduciary obligations to put your interests ahead of its own in the advisory relationship. For discretionary management, you give Clarivor limited authority to buy and sell investments in your account without asking you before each trade, subject to the objectives, guidelines, and limits agreed with you. Clarivor's services may not fit investors who want to choose each security or control the timing of each trade, who need broad financial planning, tax advice, legal advice, or who are not comfortable with the risks and complexity of options-based strategies. Clarivor asks for information about your objectives and constraints through its website so its services can be tailored to your situation."
    },
    {
        question: "How will you choose investments to recommend to me?",
        answer: "Clarivor generally uses exchange-listed options, ETFs, and stocks. Its process uses quantitative, statistical, empirical, and scenario analysis to build and monitor portfolios designed around objectives such as yield, protection, or risk-adjusted returns. Clarivor tailors its advisory services based on information you provide through its website, including objectives, constraints, risk tolerance, selected customized or model portfolios, and in some cases existing holdings. Depending on the service, clients may select customized portfolios or allocate to model portfolios designed around specified objectives and constraints. For managed accounts, Clarivor monitors client accounts daily and its software generates signals to buy or sell securities to keep accounts aligned with the client's objectives."
    },
    {
        question: "What is your relevant experience, including your licenses, education and other qualifications? What do these qualifications mean?",
        answer: <>
            Clarivor's advisory services and automated systems are overseen by Jonathan Spiegel, Clarivor's Managing
            Member and Chief Compliance Officer. Mr. Spiegel's background includes education in computer science and
            business, prior investment and risk-management roles at large financial institutions, and the CFA
            designation, which is an investment credential involving exams, investment work experience, and ethics
            standards. Public information about Clarivor and its financial professionals is available through{' '}
            <a href={investorUrl} target="_blank" rel="noopener noreferrer">investor.gov</a>.
        </>
    },
    {
        question: "Help me understand how these fees and costs might affect my investments. If I give you $10,000 to invest, how much will go to fees and costs, and how much will be invested for me?",
        answer: "For custom portfolio fee-based accounts, Clarivor charges up to 0.85% per year, billed monthly in arrears and based on the net value of assets Clarivor manages. On a $10,000 account, a 0.85% annual advisory fee would be about $85 for a full year before any fee negotiation or account value changes. The fee is normally deducted over time rather than taken all at once at signup, so the amount invested for you depends on the advisory arrangement, market movement, trading, and any other costs. Clarivor's fee does not include brokerage commissions, transaction fees, custodian charges, or ETF expenses."
    },
    {
        question: "How might your conflicts of interest affect me, and how will you address them?",
        answer: "Because Clarivor can charge asset-based fees, it has an incentive to encourage clients to increase assets managed by Clarivor. Clarivor personnel may also buy or sell securities for their own accounts that Clarivor recommends or buys for clients. Clarivor may recommend that clients use a particular non-affiliated broker-dealer for execution or custody based on factors such as costs, execution quality, tools, service, research, and convenience. That can create an incentive to consider services or research that benefit Clarivor or its clients generally, rather than only the lowest possible commission for a specific trade. Clarivor addresses conflicts through its fiduciary obligations, Code of Ethics, personal trading policies, compliance review of employee trading, policies requiring relevant client account trades before personnel trades, and its duty to seek best execution for client transactions."
    },
    {
        question: "As a financial professional, do you have any disciplinary history? For what type of conduct?",
        answer: <>
            No. Clarivor's current Form CRS and brochure state that neither Clarivor nor its financial professionals
            have reportable legal or disciplinary history. You can independently verify firm and financial professional
            information using the free search tools at{' '}
            <a href={investorUrl} target="_blank" rel="noopener noreferrer">investor.gov</a>.
        </>
    },
    {
        question: "Who is my primary contact person?",
        answer: "While Clarivor provides investment advice over the Internet, if you need help using Clarivor's website, or if you have questions about how Clarivor's models were developed or operate, you can contact Jonathan Spiegel at Clarivor at (212) 810-1187."
    },
    {
        question: "Is he or she a representative of an investment adviser or a broker-dealer?",
        answer: "Jonathan Spiegel is registered as an Investment Adviser Representative for Clarivor, an SEC-registered investment adviser. He is not a broker-dealer representative."
    },
    {
        question: "Who can I talk to if I have concerns about how this person is treating me?",
        answer: <>
            Jonathan Spiegel is also the person responsible for client service, supervision, and addressing client
            concerns. If you have concerns about how you are being treated, please contact Jonathan Spiegel directly at
            Clarivor at (212) 810-1187. Clarivor takes client concerns seriously and will review and respond to them. If
            your concern relates to brokerage or custody services, the broker-dealer or custodian may also have its own
            support and complaint process. You can also visit{' '}
            <a href={investorUrl} target="_blank" rel="noopener noreferrer">investor.gov</a> for more resources.
        </>
    },
]

function StarterCard({question, answer}) {
    return (
        <div className="border rounded p-5 mb-4 bg-white">
            <p className="font-weight-bold text-gray-900 mb-3">{question}</p>
            <p className="text-gray-800 mb-0">{answer}</p>
        </div>
    )
}

export default function FormCrsConversationStarters(props) {
    return (
        <section className="pt-8 pt-md-11 pb-8 pb-md-14 bg-light">
            <div className="container" style={{maxWidth: '880px'}}>
                <div className="row align-items-center">
                    <div className="col-12">
                        <h1 className="display-4 mb-2">
                            Form CRS Conversation Starters
                        </h1>
                        <p className="font-size-lg text-gray-700 mb-0">
                            Plain-English answers for retail investors reviewing Clarivor's Client Relationship Summary.
                        </p>
                    </div>
                </div>

                <hr className="my-6 my-md-8"/>

                <div className="alert alert-primary-soft">
                    Clarivor is an internet-based investment adviser registered with the SEC. When Clarivor acts as
                    your investment adviser, it has fiduciary obligations in its advisory relationship with you. These
                    answers are provided to address the
                    conversation starters listed in Clarivor's Form CRS. Please also review Clarivor's{' '}
                    <a href={crsUrl} target="_blank" rel="noopener noreferrer" className="font-weight-bold">
                        current Client Relationship Summary
                    </a>{' '}
                    and{' '}
                    <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="font-weight-bold">
                        Form ADV Part 2A brochure
                    </a>.
                </div>

                {starterSections.map((section) => (
                    <StarterCard key={section.question} {...section}/>
                ))}

                <div className="border-top pt-5 mt-6">
                    <p className="text-gray-800 mb-2">
                        Free and simple tools are available to research firms and financial professionals at{' '}
                        <a href={investorUrl} target="_blank" rel="noopener noreferrer" className="font-weight-bold">
                            investor.gov
                        </a>.
                    </p>
                    <p className="text-gray-800 mb-0">
                        More detailed information about Clarivor's services, fees, risks, conflicts, and disciplinary
                        history is available in the Form CRS and Form ADV Part 2A brochure linked above.
                    </p>
                </div>
            </div>
        </section>
    )
}
